import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

function getSizeWidth(size) {
  switch (size) {
    case "full":
      return "100vw";
    case "threeQuarter":
      return "75vw";
    case "half":
      return "50vw";
    case "quarter":
      return "25vw";
    default:
      return "50vw";
  }
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  'input:not([type="hidden"]):not([disabled])',
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function Modal({
  isOpen,
  onClose,
  header,
  title,
  description,
  labelledById,
  describedById,
  size = size && "half",
  closeOnOverlayClick = true,
  closeOnEsc = true,
  children,
  scrollable,
  contentScrollRef,
  portalRoot,
}) {
  const reactId = useId();
  const internalTitleId = useMemo(() => `modal-title-${reactId}`, [reactId]);
  const internalDescId = useMemo(() => `modal-desc-${reactId}`, [reactId]);

  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  const previouslyFocusedElRef = useRef(null);

  const ariaLabelledBy =
    labelledById != null
      ? labelledById
      : title != null
        ? internalTitleId
        : undefined;
  const ariaDescribedBy =
    describedById != null
      ? describedById
      : description != null
        ? internalDescId
        : undefined;

  const enhancedChildren = useMemo(() => {
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;
      if (child.type === React.Fragment) {
        return child;
      }
      return React.cloneElement(child);
    });
  }, [children]);

  // Lock scroll + focus management
  useEffect(() => {
    if (!isOpen) return;

    previouslyFocusedElRef.current = document.activeElement;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      const content = contentRef.current;
      if (!content) return;

      const focusables = Array.from(
        content.querySelectorAll(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);

      if (focusables.length > 0) {
        focusables[0].focus();
      } else {
        content.focus();
      }
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = originalOverflow;

      if (
        previouslyFocusedElRef.current &&
        previouslyFocusedElRef.current.focus
      ) {
        previouslyFocusedElRef.current.focus();
      }
      previouslyFocusedElRef.current = null;
    };
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [isOpen, closeOnEsc, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const content = contentRef.current;
      if (!content) return;

      const focusables = Array.from(
        content.querySelectorAll(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);

      if (focusables.length === 0) {
        e.preventDefault();
        content.focus();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || !content.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [isOpen]);

  if (!isOpen) return null;

  const root =
    portalRoot ?? (typeof document !== "undefined" ? document.body : null);
  if (!root) return null;

  const width = getSizeWidth(size);

  const CloseModal = () => {
    return (
      <div className={"modal-close ms-auto"} onClick={onClose}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="24"
          height="24"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M6 6L18 18M18 6L6 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  };

  return createPortal(
    <div
      ref={overlayRef}
      data-modal-overlay
      // className={overlayClassName}
      className={"modal-overlay"}
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onMouseDown={(e) => {
        if (!closeOnOverlayClick) return;
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        data-modal-wrapper
        className={"modal-wrapper"}
        style={{
          width,
          height: size === "full" ? "100vh" : !scrollable ? "auto" : "95vh",
          borderTop: "2px solid rgba(229, 9, 20, .3)",
        }}
      >
        <div
          ref={contentRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          tabIndex={-1}
          data-modal-content
          className={"modal-content"}
          style={{
            outline: "none",
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        >
          {(header || (title != null && labelledById == null)) && (
            <div data-modal-header className="modal-header">
              {header ? (
                header
              ) : (
                <div
                  id={internalTitleId}
                  data-modal-title
                  className="modal-header-copy"
                >
                  {title}
                </div>
              )}

              <CloseModal />
            </div>
          )}

          <div className={"modal-body"}>
            <div
              className={"modal-body-scroll"}
              ref={contentScrollRef}
              style={!scrollable ? { position: "relative" } : {}}
            >
              {enhancedChildren}
            </div>
          </div>
        </div>
      </div>
    </div>,
    root,
  );
}
